import { useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { debounce } from 'lodash';

import { FilterFormProps, SearchFilterProps } from '@/models/FilterConfig';
import TextInput from '@/components/ui/custom/dataInput/TextInput';

type Props = SearchFilterProps & FilterFormProps;

export default function SearchFilter(props: Props) {
  const { placeholder, name, value = '', setValue, ...config } = props;

  const [search, setSearch] = useState(value);

  const [changed, setChanged] = useState(false);

  const debouncedUpdate = useMemo(
    () =>
      debounce((value: string) => {
        setValue(name, value);
        setChanged(false);
      }, 500),
    [setValue, name],
  );

  useEffect(() => {
    if (changed) {
      if (config.debounce) {
        debouncedUpdate(search);
      } else {
        setValue(name, search);
        setChanged(false);
      }
    }

    return () => {
      debouncedUpdate.cancel();
    };
  }, [search, config.debounce, setValue, debouncedUpdate, name, changed]);

  function onClear() {
    setValue(name, undefined);
    setSearch('');
    setChanged(false);
  }

  const icon = search ? (
    <XMarkIcon className="text-error cursor-pointer" onClick={onClear} />
  ) : (
    <MagnifyingGlassIcon />
  );

  return (
    <TextInput
      type="text"
      value={search}
      onChange={({ target }) => {
        setChanged(true);
        setSearch(target.value);
      }}
      className={{ ...config.className }}
      placeholder={placeholder}
      icon={icon}
    />
  );
}
